import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Field, FieldProps, useField } from 'formik';

import Checkbox from 'common/components/checkbox';
import * as editorTypes from 'hellospa/components/editor/types';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { SectionHeader } from '../header';
import messages from './intl';
import styles from './index.scss';
import { useSignerTextInput } from 'hellospa/components/signer-signature-document/text-field-hooks';
import TextArea from 'hellospa/components/signature-request/display-field/text';
import { BannerErrorMessage } from 'hellospa/components/prep-and-send/formik-utils';
import ErrorMessage from 'hellospa/components/error-message';
import classNames from 'classnames';
import { isMultilineField } from 'hellospa/components/editor/utils';

type MergeTextFieldProps = {
  mergeField: editorTypes.TextField;
  parent: string,
  index: number,
}

type TextInputProps = {
  parent: string,
  index: number,
}
export const OVERFLOW = 'overflow';
function TextInput({ parent, index }: TextInputProps) {
  const textRef = React.useRef<TextArea>(null);
  const hiddenRef = React.useRef<TextArea>(null);
  const [field, , fieldHelpers] = useField(`${parent}.${index}.field`);
  const [, errorFieldMeta, errorHelpers] = useField(`${parent}.${index}.error`);

  const fieldData: editorTypes.TextField = field.value;

  const handleChange = React.useCallback((
    updates: Pick<editorTypes.TextField, 'fontSize' | 'value' | 'lines'>,
  ) => {
    if ('value' in updates) {
      // useSignerTextInput doesn't truncate these fields, so it doesn't
      // ever update the value.
    } else if ('fontSize' in updates || 'lines' in updates) {
      fieldHelpers.setValue({
        ...fieldData,
        ...updates,
      });
    }
  }, [fieldData, fieldHelpers]);

  const isOverflowing = useSignerTextInput(
    fieldData,
    textRef.current ? textRef.current.textRef : undefined,
    hiddenRef.current ? hiddenRef.current.textRef : undefined,
    handleChange,
    'review',
    false,
  );

  React.useEffect(() => {
    if (isOverflowing && errorFieldMeta.value !== OVERFLOW) {
      errorHelpers.setValue(OVERFLOW);
    } else if (!isOverflowing && errorFieldMeta.value === OVERFLOW) {
      errorHelpers.setValue(undefined);
    }
  }, [errorFieldMeta.value, errorHelpers, isOverflowing]);

  const focus = () => {
    textRef.current!.focus();
  };

  const isMultiline = isMultilineField(fieldData);
  const textAreaStyle = React.useMemo(() => ({
    width: `${fieldData.width}px`,
    height: `${fieldData.height}px`,
    whiteSpace: isMultiline ? 'pre-wrap' : 'pre',
    wordBreak: 'break-word',
    color: 'gray',
    fontSize: `${fieldData.fontSize}px`,
  }), [fieldData.fontSize, fieldData.height, isMultiline, fieldData.width]);
  return (
    <div className={styles.fakeTextbox} onClick={focus} >
      <TextArea
        ref={textRef}
        fieldData={fieldData}
        data-qa-ref={`${fieldData.name}.value`}
        style={textAreaStyle}
        {...field}
        id={field.name}
        // This needs to override field.onChange to only update the value property
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          fieldHelpers.setValue({
            ...fieldData,
            value: e.target.value,
          });
        }} />
      <div
        // I couldn't just use `<HiddenDisplayField`, because I'm not using a
        // whole `<DisplayField` here.
        style={{
          // This can't use display: 'none' because the purpose is to measure
          // the size of  <DisplayField.
          visibility: 'hidden',
          // This keeps it from being an invisible div you can't click through
          height: 0,
          position: 'absolute',
          top: '100%',
        }}
        aria-hidden={true}
      >
        <TextArea
          ref={hiddenRef}
          fieldData={fieldData}
          style={{ ...textAreaStyle }} />
      </div>
    </div>
  );
}


const MergeTextField = ({ mergeField, parent, index }: MergeTextFieldProps) => {
  const [field, meta] = useField(`${parent}.${index}.field`);
  return (
    <div className={classNames(styles.fields, {
      [styles.hasError]: meta.error != null && meta.touched,
    })}
    >
      <div className={styles.fieldRow}>
        <div>
          <label className={styles.mergeFieldLabel} htmlFor={field.name}>
            {mergeField.name}
          </label>
        </div>
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldContainer}>
          <TextInput parent={parent} index={index} />
        </div>
        <ErrorMessage className={styles.error} name={`${parent}.${index}.field`} />
      </div>
    </div>
  );
};

type MergeCheckboxFieldProps = {
  mergeField: editorTypes.CheckboxField,
  parent: string,
  index: number,
}

const MergeCheckboxField = ({ mergeField, parent, index }: MergeCheckboxFieldProps) => (
  <div className={styles.fields}>
    <div className={styles.fieldRow}>
      <label>
        {mergeField.name}
        <Field name={`${parent}.${index}.field`}>
          {({ field, form }: FieldProps) => (
            <Checkbox
              id={field.name}
              indeterminate={false}
              checked={field.value.checked === true}
              disabled={false}
              className=""
              onChange={() => {
                form.setFieldValue(`${field.name}.checked`, !field.value.checked);
              }}
              data-qa-ref={`${parent}.${index}.checkbox`} />
          )}
        </Field>
      </label>
    </div>
  </div>
);

const MergeFieldsSection = (() => {
  const intl = useIntl();
  const allowMergeFields = useSelector(selectors.allowMergeFields);
  const [mergeFields] = useField<types.MergeField[]>('mergeFields');

  if (!allowMergeFields || mergeFields.value.length < 1) {
    return null;
  }

  return (
    <React.Fragment>
      <BannerErrorMessage name="files" />
      <div>
        <SectionHeader text={intl.formatMessage(messages.headerMergeFields)} />
        {mergeFields.value.map((field: types.MergeField, fieldIndex: number) => {
          switch (field.field.type) {
            case editorTypes.FieldTypes.Checkbox:
              return <MergeCheckboxField
                key={fieldIndex}
                mergeField={field.field}
                parent='mergeFields'
                index={fieldIndex} />;
            case editorTypes.FieldTypes.Text:
              return <MergeTextField
                key={fieldIndex}
                mergeField={field.field}
                parent='mergeFields'
                index={fieldIndex} />;
            default: {
              return null;
            }
          }
        })}
      </div>
    </React.Fragment>
  );
});

export default MergeFieldsSection;
